// ~/apps/themusicchurch-app/frontend/src/pages/Ministries/TheMillennium.jsx

import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { FiYoutube, FiMic, FiCalendar, FiArrowLeft } from "react-icons/fi";
import ResponsiveBanner from "../../components/Common/ResponsiveBanner";

const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${(props) => props.theme.colors.homeNavy};
  color: ${(props) => props.theme.colors.homeText};

  h1, h2, h3, h4, h5, h6 { color: ${(props) => props.theme.colors.homeGold}; }
  a:focus-visible, button:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.accent};
    outline-offset: 4px;
  }
`;

// Title section with minimal padding - consistent with other pages
const TitleSection = styled.section`
  padding: ${(props) => props.theme.spacing.xl}
    ${(props) => props.theme.spacing.lg};
  text-align: center;
  background: ${(props) => props.theme.colors.homeNavy};

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: ${(props) => props.theme.spacing.lg}
      ${(props) => props.theme.spacing.lg};
  }
`;

const TitleContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  color: ${(props) => props.theme.colors.homeText};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  margin-bottom: ${(props) => props.theme.spacing.lg};
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    color: ${(props) => props.theme.colors.homeGold};
  }
`;

const MinistryTitle = styled(motion.h1)`
  font-size: ${(props) => props.theme.fontSizes["5xl"]};
  color: ${(props) => props.theme.colors.homeGold};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const MinistryTagline = styled(motion.p)`
  font-size: ${(props) => props.theme.fontSizes.xl};
  color: ${(props) => props.theme.colors.homeText};
  max-width: 600px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  padding: ${(props) => props.theme.spacing["4xl"]}
    ${(props) => props.theme.spacing.lg};
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(props) => props.theme.spacing["4xl"]};

  @media (min-width: ${(props) => props.theme.breakpoints.desktop}) {
    grid-template-columns: 2fr 1fr;
  }
`;

const MainContent = styled(motion.div)``;

const Sidebar = styled(motion.div)``;

const Section = styled.div`
  margin-bottom: ${(props) => props.theme.spacing["3xl"]};
`;

const SectionTitle = styled.h2`
  color: ${(props) => props.theme.colors.homeGold};
  font-size: ${(props) => props.theme.fontSizes["3xl"]};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const Text = styled.p`
  color: ${(props) => props.theme.colors.homeText};
  line-height: 1.8;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const QuoteBox = styled.blockquote`
  background: ${(props) => props.theme.colors.homeSurface};
  border-left: 4px solid ${(props) => props.theme.colors.accent};
  padding: ${(props) => props.theme.spacing.xl};
  margin: ${(props) => props.theme.spacing["2xl"]} 0;
  font-style: italic;
  color: ${(props) => props.theme.colors.homeText};
`;

const InfoCard = styled.div`
  background: ${(props) => props.theme.colors.homeSurface};
  border-radius: 12px;
  padding: ${(props) => props.theme.spacing.xl};
  box-shadow: ${(props) => props.theme.shadows.md};
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.md};
  margin-bottom: ${(props) => props.theme.spacing.md};

  &:last-child {
    margin-bottom: 0;
  }

  svg {
    color: ${(props) => props.theme.colors.accent};
    font-size: ${(props) => props.theme.fontSizes.xl};
  }
`;

const CTASection = styled(motion.div)`
  background: ${(props) => props.theme.colors.homeSurface};
  color: white;
  padding: ${(props) => props.theme.spacing["3xl"]};
  border-radius: 16px;
  text-align: center;
  margin-top: ${(props) => props.theme.spacing["4xl"]};
`;

const CTAButton = styled.a`
  display: inline-block;
  background: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.dark};
  padding: ${(props) => props.theme.spacing.md}
    ${(props) => props.theme.spacing["2xl"]};
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  margin-top: ${(props) => props.theme.spacing.lg};
  transition: all ${(props) => props.theme.transitions.base};

  &:hover {
    background: ${(props) => props.theme.colors.homeGold};
    transform: translateY(-2px);
  }
`;

const TheMillennium = () => {
  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>The Millennium - The Music Church</title>
        <meta
          name="description"
          content="The Millennium - YouTube weekly show and podcast discussing prophecy and the age to come."
        />
      </Helmet>

      <PageWrapper>
        {/* Title section above the banner */}
        <TitleSection>
          <TitleContent>
            {/* <BackLink to="/ministries">
              <FiArrowLeft /> Back to Ministries
            </BackLink> */}
            <MinistryTitle
              variants={titleVariants}
              initial="hidden"
              animate="visible"
            >
              The Millennium
            </MinistryTitle>
            <MinistryTagline
              variants={titleVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.2 }}
            >
              Exploring prophecy and the age to come
            </MinistryTagline>
          </TitleContent>
        </TitleSection>

        {/* Banner using ResponsiveBanner */}
        <ResponsiveBanner
          src="/images/home-preview/millennium-framed-v1.png"
          alt="The Millennium - YouTube weekly show and podcast"
          maxWidth="800px"
          motionProps={{
            initial: { opacity: 0, y: 30 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.8, delay: 0.3 },
          }}
        />

        <ContentSection>
          <ContentGrid>
            <MainContent
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <Section>
                <SectionTitle>About The Millennium</SectionTitle>
                <Text>
                  The Millennium is our YouTube weekly show and podcast
                  featuring panel discussions on this present age and the age to
                  come. The prophecy speaks of 1000 years of peace and
                  productivity.
                </Text>
                <Text>
                  The show challenges us to expand our imagination, to expect
                  the future as prophesied in Isaiah 11:6-9. A time when weapons
                  of war will be transformed into tools of agriculture,
                  symbolizing a shift from conflict and violence to a time of
                  peace and harmony.
                </Text>
              </Section>

              <QuoteBox>
                "They will beat their swords into plowshares and their spears
                into pruning hooks. Nation will not take up sword against
                nation, nor will they train for war anymore."
                <br />- Isaiah 2:4
              </QuoteBox>

              <Section>
                <SectionTitle>What We Discuss</SectionTitle>
                <Text>
                  Each week, our panel explores biblical prophecy, current
                  events through a prophetic lens, and the hope of Christ's
                  millennial reign. Topics include:
                </Text>
                <ul
                  style={{
                    marginLeft: "2rem",
                    color: "#c6d9e2",
                    lineHeight: "2",
                  }}
                >
                  <li>Biblical prophecy and its modern-day relevance</li>
                  <li>Signs of the times and current world events</li>
                  <li>The promise of restoration and reconciliation</li>
                  <li>Preparing our hearts for His return</li>
                  <li>Living with eternal perspective in temporary times</li>
                </ul>
              </Section>

              <Section>
                <SectionTitle>The Vision</SectionTitle>
                <Text>
                  We envision a future where even the natural enemies in the
                  animal kingdom will live together peacefully. This is the
                  picture of restoration and reconciliation that God promises to
                  bring about in the world. Through our discussions, we aim to
                  inspire hope and preparation for this glorious future.
                </Text>
              </Section>
            </MainContent>

            <Sidebar
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Join Us
                </SectionTitle>
                <InfoItem>
                  <FiYoutube />
                  <span>YouTube: Weekly Episodes</span>
                </InfoItem>
                <InfoItem>
                  <FiMic />
                  <span>Podcast: Available on all platforms</span>
                </InfoItem>
                <InfoItem>
                  <FiCalendar />
                  <span>New episodes every Thursday</span>
                </InfoItem>
              </InfoCard>

              <InfoCard>
                <SectionTitle style={{ fontSize: "1.5rem" }}>
                  Key Scripture
                </SectionTitle>
                <Text style={{ fontSize: "0.875rem", marginBottom: "0" }}>
                  "The wolf will live with the lamb, the leopard will lie down
                  with the goat, the calf and the lion and the yearling
                  together; and a little child will lead them."
                </Text>
                <Text style={{ fontSize: "0.75rem", opacity: 0.7 }}>
                  - Isaiah 11:6
                </Text>
              </InfoCard>
            </Sidebar>
          </ContentGrid>

          <CTASection
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h3 style={{ fontSize: "2rem", marginBottom: "1rem" }}>
              Ready to Explore the Future?
            </h3>
            <p>
              Join us every week as we discuss the hope and promise of the
              millennium to come.
            </p>
            <CTAButton href="#" target="_blank">
              Subscribe on YouTube
            </CTAButton>
          </CTASection>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default TheMillennium;
